import { useEffect, useRef } from "react";
import type { AnalyticsSettings } from "@/hooks/useAnalyticsSettings";

/**
 * Persists an analytics setting once the local value has settled for 100ms.
 *
 * The skip-if-unchanged check compares against the last value this hook
 * synced (seeded with the first committed value), NOT the live settings
 * object: re-arming on every persisted-settings write would feed straight
 * back into the persisted state — the loop that previously forced the inline
 * effect to omit its settings dependency behind an eslint-disable.
 */
export function useDebouncedSetting<K extends keyof AnalyticsSettings>(
  value: AnalyticsSettings[K],
  settingKey: K,
  updateSetting: (key: K, value: AnalyticsSettings[K]) => void,
): void {
  const lastSyncedValueRef = useRef<AnalyticsSettings[K]>(value);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (value !== lastSyncedValueRef.current) {
        lastSyncedValueRef.current = value;
        updateSetting(settingKey, value);
      }
    }, 100); // Shorter debounce for better responsiveness

    return () => clearTimeout(timeoutId);
  }, [value, settingKey, updateSetting]);
}
